"use client";

/**
 * Slider 컴포넌트
 * @description 가로 스크롤 리스트. 좌우 패딩과 항목 간 갭을 `Section` 과 같은 축으로 받아
 *   한 화면 안의 가로 리듬이 갈라지지 않게 한다. 앱 `shared/ui/Slider` 의 웹 포팅 —
 *   마우스 끌기, 넓은 화면의 좌우 버튼과 끝 감지는 웹이 더한 것이다.
 * @example
 * <Slider padding='sm' data={collabs} keyExtractor={c => String(c.collabId)}
 *   renderItem={c => <ContentCard … />} />
 */
import { IconButton } from "@fanwork-web/design-system";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { memo, useId } from "react";

import { sliderStyles } from "./Slider.styles";
import type { SliderProps } from "./Slider.types";
import { useEndReached } from "./useEndReached";
import { useSliderNavigation } from "./useSliderNavigation";

import { useDragScroll } from "../DragScrollRow";

function SliderInner<T>({
  data,
  renderItem,
  keyExtractor,
  padding = "lg",
  gap = "sm",
  bleed = "none",
  navigationLabel,
  onEndReached,
  ListEmptyComponent,
  testID,
}: SliderProps<T>) {
  const { ref, handlers } = useDragScroll<HTMLDivElement>();
  const sentinelRef = useEndReached(ref, onEndReached);
  const trackId = useId();
  const { canPrevious, canNext, scroll } = useSliderNavigation(
    ref,
    data,
    Boolean(navigationLabel),
  );

  if (data.length === 0) return <>{ListEmptyComponent ?? null}</>;

  return (
    <div className={sliderStyles.frame({ padding })}>
      <div
        ref={ref}
        id={trackId}
        {...handlers}
        className={sliderStyles.root({ padding, gap, bleed })}
        role={navigationLabel ? "region" : undefined}
        aria-label={navigationLabel}
        data-testid={testID}
      >
        {data.map((item, index) => (
          <div key={keyExtractor(item, index)} className={sliderStyles.item}>
            {renderItem(item, index)}
          </div>
        ))}
        {onEndReached ? (
          <div ref={sentinelRef} aria-hidden className={sliderStyles.sentinel} />
        ) : null}
      </div>
      {navigationLabel && (canPrevious || canNext) ? (
        <>
          <div
            className={`${sliderStyles.control} ${sliderStyles.controlPrevious}`}
          >
            <IconButton
              icon={<ChevronLeft aria-hidden />}
              label={`Scroll ${navigationLabel} left`}
              aria-controls={trackId}
              tone="neutral"
              size="sm"
              radius="circle"
              disabled={!canPrevious}
              onClick={() => scroll(-1)}
            />
          </div>
          <div
            className={`${sliderStyles.control} ${sliderStyles.controlNext}`}
          >
            <IconButton
              icon={<ChevronRight aria-hidden />}
              label={`Scroll ${navigationLabel} right`}
              aria-controls={trackId}
              tone="neutral"
              size="sm"
              radius="circle"
              disabled={!canNext}
              onClick={() => scroll(1)}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

export const Slider = memo(SliderInner) as typeof SliderInner;
